import { size } from 'constants/device';
import { APP_ROUTES } from 'constants/routes';

import { useState } from 'react';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import { Divider, Headings } from '@studenten/ui-components';
import {
  JobCard,
  CompanyCard,
} from 'features/layout/components/Cards';
import SelectedFilters from 'features/search/components/SelectedFilters';
import { useStore } from 'features/search/models/generalSearch';
import PageBanner from 'features/layout/components/PageBanner';
import SearchResultsHeader from 'features/search/components/SearchResultsHeader';
import MobileFiltersOpenButton from 'features/search/components/MobileFiltersOpenButton';
import { usePopRouterEventNotShallow } from 'features/search/hooks/usePopRouterEventNotShallow';
import SearchFilters from 'features/search/components/SearchFilters';
import { useWindowSize } from 'features/layout/hooks/useWindowSize';
import EmptySearch from 'features/search/components/EmptySearch';
import {
  Page,
  MobileShowAllWrapper,
  FiltersAndResults,
  Results,
  ResultsList,
  ResultItem,
  ResultSection,
  ResultsTitleWrapper,
} from 'features/search/templates/Search.styled';
import { ShowAll } from 'features/search/components/ShowAll';
import Layout from 'features/layout/templates/Layout';
import { VacancyTypes } from 'api_entities/vacancies/types';
import { MetaTags } from 'lib/seo/MetaTags';

import { generalSearchFilters } from './filters';
import { Props } from './types';

export const GeneralSearch = observer(({ data, filters }: Props) => {
  usePopRouterEventNotShallow();
  const store = useStore(data, filters);
  const router = useRouter();
  const { width: windowWidth } = useWindowSize();
  const {
    totalCount,
    jobs,
    companies,
  } = store.data;

  const [isMobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(false);
  const isMobile = windowWidth < size.tablet;
  const withSearchResults = Boolean(totalCount);
  return (
    <>
      <MetaTags
        title="Zoeken op Studenten.nl"
        description="Zoekpagina van de website Studenten.nl "
      />
      <Layout>
        <Page>
          <PageBanner title={router.query.query as string} />
          <Divider />
          <section id="searchResults">
            <SearchResultsHeader title={router.query.query as string} totalCount={totalCount} />
            <MobileFiltersOpenButton
              onFilterOpen={() => setMobileFiltersOpen(true)}
              filters={store.filters}
            />
            <FiltersAndResults>
              <SearchFilters
                filters={generalSearchFilters}
                store={store}
                totalCount={totalCount}
                isOpen={isMobileFiltersOpen}
                setOpen={setMobileFiltersOpen}
              />

              <Results>
                <SelectedFilters
                  filters={store.filters}
                  handleDeleteFilter={store.deleteSelectedFilter.bind(store)}
                  handleDeleteAllFilters={store.deleteAllSelectedFilters.bind(store)}
                />
                <div>
                  {withSearchResults ? (
                    <div>
                      {!!jobs?.totalCount && (
                        <ResultSection>
                          <ResultsTitleWrapper>
                            <Headings.H2>Banen</Headings.H2>
                            {!isMobile && (
                              <ShowAll route={APP_ROUTES.JOBS} totalCount={jobs?.totalCount} />
                            )}
                          </ResultsTitleWrapper>
                          <ResultsList>
                            {jobs.data.map((props) => (
                              <ResultItem key={props.objectID}>
                                <JobCard
                                  vacancyType={VacancyTypes.job}
                                  horizontal={!isMobile}
                                  {...props}
                                />
                              </ResultItem>
                            ))}
                          </ResultsList>
                          {isMobile && (
                            <MobileShowAllWrapper>
                              <ShowAll route={APP_ROUTES.JOBS} totalCount={jobs?.totalCount} />
                            </MobileShowAllWrapper>
                          )}
                        </ResultSection>
                      )}
                      {!!companies?.totalCount && (
                        <ResultSection>
                          <ResultsTitleWrapper>
                            <Headings.H2>Companies</Headings.H2>
                            {!isMobile && (
                              <ShowAll
                                route={APP_ROUTES.COMPANIES}
                                totalCount={companies?.totalCount}
                              />
                            )}
                          </ResultsTitleWrapper>
                          <ResultsList>
                            {companies.data.map((props) => (
                              <ResultItem key={props.objectID}>
                                <CompanyCard horizontal {...props} />
                              </ResultItem>
                            ))}
                          </ResultsList>
                          {isMobile && (
                            <MobileShowAllWrapper>
                              <ShowAll
                                route={APP_ROUTES.COMPANIES}
                                totalCount={companies?.totalCount}
                              />
                            </MobileShowAllWrapper>
                          )}
                        </ResultSection>
                      )}
                    </div>
                  ) : (
                    <EmptySearch />
                  )}
                </div>
              </Results>
            </FiltersAndResults>
          </section>
        </Page>
      </Layout>
    </>
  );
});
